import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import toast from "react-hot-toast"
import nProgress from "nprogress"

import { useAuth } from "contexts/auth-context"

/**
 * 270726 (permintaan user) — Master "Customer Penagihan Outlet".
 *
 * Hutang toko ke HO dicatat di menu Catatan Customer yang berbasis CUSTOMER,
 * sementara PO hanya menyimpan outlet tujuan. Layar ini memasangkan keduanya
 * sekali di awal: tiap outlet toko → satu customer di Master Customer HO.
 *
 * Setelah dipasangkan:
 *   - PO dari HO ke outlet itu di-approve  → terbit nota DEBIT (hutang) senilai PO
 *   - customer di outlet itu bayar pakai point → terbit nota KREDIT (pengurang hutang)
 * Outlet tanpa pasangan tetap bisa bertransaksi — hanya nota hutangnya tidak terbit.
 */

const apiBase = () => (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")

const inputClass =
  "w-full px-3 py-2 text-sm border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"

/** Modal cari customer di Master Customer HO (sumber yang sama dengan pasangan). */
function CustomerPicker({ open, onClose, onSelect, outletName }) {
  const searchRef = useRef(null)
  const debounceRef = useRef(null)

  const [searchValue, setSearchValue] = useState("")
  const [search, setSearch] = useState("")
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setSearchValue("")
    setSearch("")
    const t = setTimeout(() => searchRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [open])

  useEffect(() => () => clearTimeout(debounceRef.current), [])

  useEffect(() => {
    if (!open) return

    const ctrl = new AbortController()
    const run = async () => {
      try {
        setIsLoading(true)
        const res = await axios.get(`${apiBase()}/customer-master`, {
          params: { search: search || undefined, per_page: 25 },
          signal: ctrl.signal,
        })
        setRows(Array.isArray(res?.data?.data) ? res.data.data : [])
      } catch (e) {
        if (!axios.isCancel(e)) setRows([])
      } finally {
        setIsLoading(false)
      }
    }

    run()
    return () => ctrl.abort()
  }, [open, search])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-3xl bg-white shadow-2xl rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Pilih Customer Penagihan</h3>
            <p className="text-sm text-neutral-500">
              Untuk outlet <b>{outletName}</b> — hutang outlet ini akan tercatat atas nama customer terpilih.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          <input
            ref={searchRef}
            value={searchValue}
            onChange={(e) => {
              const val = e.target.value
              setSearchValue(val)
              clearTimeout(debounceRef.current)
              debounceRef.current = setTimeout(() => setSearch(val.trim()), 400)
            }}
            placeholder="Cari kode / nama customer..."
            className={inputClass}
          />

          <div className="overflow-hidden border rounded-2xl">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 text-xs font-semibold border-b bg-neutral-50 text-neutral-600">
              <div className="col-span-3">Kode</div>
              <div className="col-span-7">Nama</div>
              <div className="col-span-2" />
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">Memuat customer...</div>
              ) : !rows.length ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">Customer tidak ditemukan.</div>
              ) : (
                rows.map((row) => (
                  <div
                    key={row.yoiboi_customer_id}
                    className="grid items-center grid-cols-12 gap-3 px-4 py-3 text-sm border-b last:border-b-0 hover:bg-neutral-50"
                  >
                    <div className="col-span-3 font-mono text-xs text-neutral-600">{row.kode}</div>
                    <div className="col-span-7 font-medium truncate text-neutral-900">{row.name}</div>
                    <div className="flex justify-end col-span-2">
                      <button
                        type="button"
                        onClick={() => onSelect(row)}
                        className="px-3 py-2 text-xs font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
                      >
                        Pilih
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function OutletCustomerMappingList() {
  // 270726: node permission halaman ini di DB generic —
  // outlet-customer-mapping.view (buka menu) & .update (ubah pasangan).
  const { can } = useAuth()
  const canUpdate = can?.("outlet-customer-mapping.update")

  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [pickerFor, setPickerFor] = useState(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setIsLoading(true)
      nProgress.start()
      const res = await axios.get(`${apiBase()}/outlet-customer-mapping`)
      setRows(Array.isArray(res?.data?.data) ? res.data.data : [])
    } catch (e) {
      toast.error(e?.response?.data?.message || "Gagal memuat daftar outlet")
    } finally {
      setIsLoading(false)
      nProgress.done()
    }
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.outlet_name, r.outlet_code, r.customer_name, r.customer_kode]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    )
  }, [rows, search])

  const pairedCount = useMemo(() => rows.filter((r) => r.customer_id).length, [rows])

  const savePair = async (outletId, customer) => {
    try {
      setSaving(true)
      nProgress.start()
      await axios.put(`${apiBase()}/outlet-customer-mapping/${outletId}`, {
        customer_id: customer.yoiboi_customer_id,
      })
      toast.success("Pasangan customer disimpan")
      setPickerFor(null)
      await load()
    } catch (e) {
      toast.error(e?.response?.data?.message || "Gagal menyimpan pasangan customer")
    } finally {
      setSaving(false)
      nProgress.done()
    }
  }

  const removePair = async (row) => {
    if (!window.confirm(
      `Lepas pasangan customer untuk ${row.outlet_name}?\n\n` +
      "Setelah dilepas, PO ke outlet ini tidak lagi menerbitkan nota hutang."
    )) return

    try {
      setSaving(true)
      nProgress.start()
      await axios.delete(`${apiBase()}/outlet-customer-mapping/${row.outlet_id}`)
      toast.success("Pasangan customer dilepas")
      await load()
    } catch (e) {
      toast.error(e?.response?.data?.message || "Gagal melepas pasangan customer")
    } finally {
      setSaving(false)
      nProgress.done()
    }
  }

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <h2 className="text-lg font-semibold text-neutral-900">Customer Penagihan Outlet</h2>
        <p className="mt-1 text-sm text-neutral-500">
          Pasangkan tiap outlet toko dengan customer di Master Customer HO. Pasangan ini dipakai
          mencatat hutang toko ke HO: PO yang di-approve menambah hutang, pembayaran point customer
          dan CN dari HO menguranginya — semuanya tampil di menu Catatan Customer.
        </p>
        <p className="mt-2 text-xs text-neutral-500">
          Sudah dipasangkan: <b>{pairedCount}</b> dari {rows.length} outlet.
        </p>
      </div>

      <div className="bg-white border rounded-2xl border-neutral-200">
        <div className="p-4 border-b border-neutral-200">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari outlet / customer..."
            className={`sm:w-96 ${inputClass}`}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">Outlet</th>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">Entitas</th>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">Customer Penagihan</th>
                <th className="px-4 py-3 text-xs font-medium text-right uppercase text-neutral-500">Aksi</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-sm text-center text-neutral-500">
                    Memuat daftar outlet...
                  </td>
                </tr>
              ) : !filtered.length ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-sm text-center text-neutral-500">
                    Outlet tidak ditemukan.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.outlet_id} className="hover:bg-brand-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-neutral-900">{row.outlet_name}</div>
                      <div className="text-[11px] text-neutral-500">
                        #{row.outlet_id} {row.outlet_code ? `• ${row.outlet_code}` : ""}
                        {row.is_ho ? " • HO" : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-600">{row.entity || "-"}</td>
                    <td className="px-4 py-3 text-sm">
                      {row.customer_id ? (
                        <>
                          <div className="font-medium text-neutral-900">{row.customer_name || "-"}</div>
                          <div className="text-[11px] text-neutral-500">
                            {row.customer_kode ? `${row.customer_kode} • ` : ""}ID {row.customer_id}
                          </div>
                        </>
                      ) : (
                        <span className="text-xs italic text-neutral-400">Belum dipasangkan</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        disabled={saving || !canUpdate}
                        title={canUpdate ? undefined : "Tidak punya izin mengubah pasangan customer"}
                        onClick={() => setPickerFor(row)}
                        className="px-3 py-2 text-xs font-semibold border rounded-lg border-brand-900 text-neutral-900 hover:bg-neutral-100 disabled:opacity-40"
                      >
                        {row.customer_id ? "Ganti" : "Pasangkan"}
                      </button>
                      {row.customer_id && canUpdate && (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => removePair(row)}
                          className="px-3 py-2 ml-2 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-40"
                        >
                          Lepas
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CustomerPicker
        open={Boolean(pickerFor)}
        outletName={pickerFor?.outlet_name}
        onClose={() => setPickerFor(null)}
        onSelect={(customer) => savePair(pickerFor.outlet_id, customer)}
      />
    </div>
  )
}
